import { useMemo } from 'react';
import { useBetaFeatures } from '@/entities/beta-feature';
import { useMyProfile } from '@/entities/profile';
import { RouteConfig } from '@/shared/lib';
import { findFirstMenuPath } from '../utils';
import { useIsGroupVisible } from './useIsGroupVisible';
import { useIsRouteVisible } from './useIsRouteVisible';

/**
 * Адрес первого пункта меню, видимого текущему пользователю, — куда вести его, когда адрес
 * раздела не задан.
 *
 * Видимость зависит от разрешений профиля и реестра бета-функций: пока они не получены,
 * неполученное неотличимо от закрытого, и выбор увёл бы не туда — поэтому вместе с адресом
 * возвращается признак готовности, и до него решение не принимается.
 *
 * Возвращает: `path` — адрес пункта (`undefined`, если в меню пользователю не видно ничего),
 * `isResolved` — профиль и реестр получены (или попытка завершилась).
 */
export const useFirstMenuPath = (menuItems: readonly RouteConfig[]) => {
  const { isFetched: isProfileFetched } = useMyProfile();
  const { isFetched: isBetaFeaturesFetched } = useBetaFeatures();
  const isRouteVisible = useIsRouteVisible();
  const isGroupVisible = useIsGroupVisible();

  const path = useMemo(
    () => findFirstMenuPath(menuItems, isRouteVisible, isGroupVisible),
    [menuItems, isRouteVisible, isGroupVisible],
  );

  return { path, isResolved: isProfileFetched && isBetaFeaturesFetched };
};
